import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { fetchCertifications } from '@/api/certifications';
import { CertificationsTable } from '@/components/shared/CertificationsTable';
import { PageContainer } from '@/components/shared/PageContainer';
import { certificationListKeys } from '@/hooks/useCertificationsQuery';
import { queryClient } from '@/lib/utils/queryClient';
import { useSessionStore } from '@/store/sessionStore';

// Default list order: soonest-to-expire first. Must match the `defaultSort`
// in useCertificationsTable so the loader prefetch and the live query share a
// cache key (avoids an unsorted first paint + double fetch).
const DEFAULT_SORT = 'expirationDate:ASC';

const searchSchema = z.object({
  page: z.number().int().min(1).optional(),
  limit: z.number().int().min(10).max(500).optional(),
  sort: z.string().optional(),
  filters: z.record(z.string(), z.array(z.string())).optional(),
  dialog: z
    .enum(['create-certification-manual', 'create-certification-ai'])
    .optional(),
});

export const Route = createFileRoute('/(client)/certifications/(pages)/')({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({
    page: search.page ?? 1,
    limit: search.limit ?? 50,
    sort: search.sort ?? DEFAULT_SORT,
  }),
  loader: ({ deps }) => {
    // Empty companyId — this page lists all certifications scoped by auth,
    // not by an explicit companyId param.
    return queryClient.ensureQueryData({
      queryKey: certificationListKeys.list('', {
        page: deps.page,
        count: deps.limit,
        sort: deps.sort,
      }),
      queryFn: () => {
        const qs = new URLSearchParams({
          isCertification: 'true',
          page: String(deps.page - 1),
          count: String(deps.limit),
          sort: deps.sort,
          filter: '{}',
        });
        return fetchCertifications(qs.toString());
      },
    });
  },
  component: CertificationsPage,
});

function CertificationsPage() {
  const user = useSessionStore(s => s.user);
  const companyName = user?.company?.name;

  return (
    <PageContainer>
      <CertificationsTable
        companyId=''
        isOwnCompany={true}
        {...(companyName !== undefined ? { companyName } : {})}
      />
    </PageContainer>
  );
}
